import { useState, useEffect, useCallback, useMemo } from 'react';
import { getItems, YARDS_TO_METERS } from './fitnessApi';

const WORKOUTS_LIST = 'SwimmingWorkouts';
const MS_DAY  = 24 * 60 * 60 * 1000;
const MS_WEEK = 7 * MS_DAY;
const MS_90   = 90 * MS_DAY;

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

function medianOf(arr) {
  if (arr.length === 0) return null;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function formatPace(sec) {
  if (sec == null || sec <= 0) return '--:--.-';
  const m = Math.floor(sec / 60);
  const s = (sec % 60).toFixed(1);
  return `${m}:${s.padStart(4, '0')}`;
}

function formatRepTime(sec) {
  if (sec <= 0) return '--:--';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatClock(sec) {
  const s = Math.round(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    : `${m}:${String(ss).padStart(2, '0')}`;
}

// Moving % — how much of a session was actually spent swimming.
//   swimSeconds    = Σ of every repTimes entry across all sets
//   elapsedSeconds = ActiveSeconds when present (wall-clock, written only by
//                    History's editor — see workoutEdit.js), else
//                    WorkoutEndTime − WorkoutStartTime, else DurationSeconds
//   movingPct      = swimSeconds / elapsedSeconds
// Partial sets ARE counted: the swimming happened even though the set was cut
// short. This deliberately differs from the pace/PR paths, which skip them
// because a mistimed split corrupts a pace but not a sum.
// Sets without repTimes contribute 0 — their distance still happened, so this
// is a normal case, not an error.
// Elapsed comes only from the stamps or DurationSeconds, never WorkoutDate:
// some rows (e.g. 7/26) carry an end stamp that disagrees with WorkoutDate.
// Returns null when neither source yields a positive elapsed, so the session is
// excluded from the metric rather than zero-filled or fabricated.
export function computeMoving(w) {
  let swimSeconds = 0;
  (w.sets || []).forEach(s => {
    if (!Array.isArray(s.repTimes)) return;
    s.repTimes.forEach(t => {
      const n = Number(t);
      if (n > 0) swimSeconds += n;
    });
  });

  const start = w.startTime ? new Date(w.startTime).getTime() : NaN;
  const end   = w.endTime   ? new Date(w.endTime).getTime()   : NaN;

  let elapsedSeconds = null;
  let elapsedSource  = null;
  if (Number(w.activeSeconds) > 0) {
    elapsedSeconds = Number(w.activeSeconds);
    elapsedSource  = 'active';
  } else if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
    elapsedSeconds = (end - start) / 1000;
    elapsedSource  = 'stamps';
  } else if (Number(w.durationSeconds) > 0) {
    elapsedSeconds = Number(w.durationSeconds);
    elapsedSource  = 'duration';
  }
  if (elapsedSeconds === null || elapsedSeconds <= 0) return null;

  const rawPct = swimSeconds / elapsedSeconds;
  return {
    swimSeconds,
    elapsedSeconds,
    elapsedSource,
    movingPct: Math.min(rawPct, 1), // clamp display; the raw overflow is flagged below
    overflow: rawPct > 1,
  };
}

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Purple brand ramp; thresholds are meter-anchored regardless of display unit
function swimColor(meters) {
  if (meters <= 0)    return null;
  if (meters <= 500)  return '#c4a8e0';
  if (meters <= 1100) return '#9b6fd4';
  if (meters <= 1800) return '#6b35c2';
  return '#4B0082';
}

function getSundayOf(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

function computeStreaks(sessions) {
  const swimWeeks = new Set();
  sessions.forEach(s => swimWeeks.add(getSundayOf(new Date(s.date)).getTime()));

  // Current streak: walk back week-by-week from this Sunday
  const thisSunday = getSundayOf(new Date());
  let current = 0;
  const check = new Date(thisSunday);
  while (swimWeeks.has(check.getTime())) {
    current++;
    check.setDate(check.getDate() - 7);
    check.setHours(0, 0, 0, 0); // re-normalise after potential DST crossing
  }

  // Longest streak: scan sorted week timestamps with DST-safe consecutive test
  const sorted = Array.from(swimWeeks).sort((a, b) => a - b);
  let longest = 0, run = 0;
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      run = 1;
    } else {
      const prev = new Date(sorted[i - 1]);
      prev.setDate(prev.getDate() + 7);
      prev.setHours(0, 0, 0, 0);
      run = prev.getTime() === sorted[i] ? run + 1 : 1;
    }
    if (run > longest) longest = run;
  }

  return { currentStreak: current, longestStreak: longest };
}

// ── SVG layout constants (pace + volume charts) ──────────────────────────────
const SVG_W   = 600;
const PAD_L   = 44;
const PAD_R   = 12;
const PAD_T   = 18;
const CW      = SVG_W - PAD_L - PAD_R;

const P_SVG_H = 170;
const P_PAD_B = 38;
const PCH     = P_SVG_H - PAD_T - P_PAD_B;

const V_SVG_H = 130;
const V_PAD_B = 34;
const VCH     = V_SVG_H - PAD_T - V_PAD_B;

function labelIndices(n, maxLabels = 6) {
  if (n <= maxLabels) return Array.from({ length: n }, (_, i) => i);
  const step = Math.ceil(n / maxLabels);
  const idx = Array.from({ length: n }, (_, i) => i).filter(i => i % step === 0);
  if (idx[idx.length - 1] !== n - 1) idx.push(n - 1);
  return idx;
}

// ── MAIN DASHBOARD ───────────────────────────────────────────────────────────

function SwimmingDashboard({ getToken, userEmail }) {
  const [workouts, setWorkouts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [displayUnit, setDisplayUnit] = useState(
    () => localStorage.getItem('dh_displayUnit') || 'm'
  );

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const raw   = await getItems(token, WORKOUTS_LIST);
      const mine  = raw
        .filter(w => w.CreatedByEmail === userEmail)
        .map(w => ({
          id:                  w.id,
          sets:                safeParse(w.Sets),
          templateName:        w.TemplateName || w.Title || '',
          totalDistanceMeters: w.TotalDistanceMeters || 0,
          durationSeconds:     w.DurationSeconds || 0,
          activeSeconds:       w.ActiveSeconds || 0,
          date:                w.WorkoutDate || w.Created || '',
          startTime:           w.WorkoutStartTime || '',
          endTime:             w.WorkoutEndTime || '',
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setWorkouts(mine);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken, userEmail]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function toggleUnit() {
    const next = displayUnit === 'm' ? 'yd' : 'm';
    setDisplayUnit(next);
    localStorage.setItem('dh_displayUnit', next);
  }

  function convertDist(meters) {
    return displayUnit === 'yd' ? Math.round(meters / YARDS_TO_METERS) : Math.round(meters);
  }

  // sec/100m → sec/100yd: 100yd = 91.44m so time = pace × 0.9144
  function paceToDisplay(secPer100m) {
    return displayUnit === 'yd' ? secPer100m * YARDS_TO_METERS : secPer100m;
  }

  const sessions = useMemo(() => {
    return workouts.map(w => {
      const freeReps = []; // {pace, stroke, perRepMeters, repTimeSec}

      w.sets.forEach(s => {
        // Skip sets flagged as mistimed / cut short — their reps aren't valid pace candidates.
        if (s.partial === true) return;

        const stroke = (s.actualStroke || s.stroke || '').trim();
        if (stroke.toLowerCase() !== 'free') return;

        const reps           = Number(s.actualReps ?? s.reps ?? 1);
        const totalSetMeters = Number(s.distanceMeters) || 0;
        if (totalSetMeters <= 0 || reps <= 0) return;
        const perRepMeters   = totalSetMeters / reps;

        const repTimes = Array.isArray(s.repTimes)
          ? s.repTimes.map(Number).filter(t => t > 0)
          : [];

        if (repTimes.length > 0) {
          if (repTimes.length !== reps) {
            console.warn(
              `SwimDash: repTimes.length (${repTimes.length}) !== reps (${reps}) ` +
              `[${reps}×${s.distance ?? '?'} ${s.stroke ?? '?'}, ` +
              `${new Date(w.date).toLocaleDateString()}] — skipping pace for this set`
            );
            return;
          }
          // Drop implausibly-fast outliers relative to THIS set's median (same guard as PRs)
          // so a mistimed split can't poison bestFreePace. Needs >=2 reps for a meaningful median.
          let validTimes = repTimes;
          if (repTimes.length >= 2) {
            const median = medianOf(repTimes);
            validTimes = repTimes.filter(t => t >= median * 0.6);
          }
          validTimes.forEach(t => freeReps.push({ pace: (t / perRepMeters) * 100, stroke, perRepMeters, repTimeSec: t }));
        } else if (reps === 1 && Number(s.timeSeconds) > 0) {
          const t = Number(s.timeSeconds);
          freeReps.push({ pace: (t / perRepMeters) * 100, stroke, perRepMeters, repTimeSec: t });
        }
      });

      const freePaces = freeReps.map(r => r.pace);
      const med       = medianOf(freePaces);

      let medianSetInfo = null;
      if (med !== null && freeReps.length > 0) {
        const closest = freeReps.reduce((best, r) =>
          Math.abs(r.pace - med) < Math.abs(best.pace - med) ? r : best
        , freeReps[0]);
        medianSetInfo = { stroke: closest.stroke, perRepMeters: closest.perRepMeters, repTimeSec: closest.repTimeSec };
      }

      return {
        ...w,
        bestFreePace:   freePaces.length > 0 ? Math.min(...freePaces) : null,
        medianFreePace: med,
        medianSetInfo,
        moving:         computeMoving(w), // null when the session has no usable elapsed
      };
    });
  }, [workouts]);

  const totalMeters    = sessions.reduce((s, w) => s + w.totalDistanceMeters, 0);
  const totalSessions  = sessions.length;
  const weekCutoff     = new Date();
  weekCutoff.setDate(weekCutoff.getDate() - 7);
  const thisWeekMeters = sessions
    .filter(w => new Date(w.date) >= weekCutoff)
    .reduce((s, w) => s + w.totalDistanceMeters, 0);

  const paceSessions = sessions.filter(s => s.bestFreePace !== null);
  const latestPace   = paceSessions[paceSessions.length - 1] ?? null;
  const prevPace     = paceSessions[paceSessions.length - 2] ?? null;
  const paceDeltaSec = latestPace && prevPace
    ? latestPace.bestFreePace - prevPace.bestFreePace
    : null;

  const paceChartData = sessions.filter(s => s.medianFreePace !== null);

  // Moving % — sessions with no usable elapsed are excluded outright, never zero-filled
  const movingSessions = sessions.filter(s => s.moving !== null);
  const movingCutoff   = new Date();
  movingCutoff.setDate(movingCutoff.getDate() - 30);
  const moving30 = movingSessions.filter(s => new Date(s.date) >= movingCutoff);
  const moving30Avg = moving30.length > 0
    ? moving30.reduce((sum, s) => sum + s.moving.movingPct, 0) / moving30.length
    : null;
  const movingExcluded = sessions.length - movingSessions.length;

  if (loading) return <p className="Fitness-loading">Loading dashboard...</p>;

  if (totalSessions === 0) {
    return (
      <div className="SwimDash">
        <p className="Fitness-empty">Log your first swim to see your dashboard.</p>
      </div>
    );
  }

  return (
    <div className="SwimDash">
      {error && <p className="Fitness-error">{error}</p>}

      <button className="Fitness-btn Fitness-btn--small SwimDash-unit-toggle" onClick={toggleUnit}>
        Showing: {displayUnit === 'm' ? 'Meters' : 'Yards'}
      </button>

      {/* HERO */}
      <div className="SwimDash-hero">
        <div className={`SwimDash-hero-pace${!latestPace ? ' SwimDash-hero-pace--empty' : ''}`}>
          {latestPace ? formatPace(paceToDisplay(latestPace.bestFreePace)) : '--:--.-'}
        </div>
        <div className="SwimDash-hero-label">
          per 100{displayUnit} · Freestyle · best rep, most recent session
        </div>
        {latestPace && paceDeltaSec !== null && (
          <div className={`SwimDash-hero-delta ${paceDeltaSec < 0 ? 'SwimDash-hero-delta--faster' : 'SwimDash-hero-delta--slower'}`}>
            {paceDeltaSec < 0
              ? `−${paceToDisplay(Math.abs(paceDeltaSec)).toFixed(1)}s faster than last`
              : `+${paceToDisplay(Math.abs(paceDeltaSec)).toFixed(1)}s slower than last`}
          </div>
        )}
        {!latestPace && (
          <p className="SwimDash-hero-hint">
            Log rep splits during Freestyle sets to unlock pace tracking
          </p>
        )}
      </div>

      {/* TOTALS */}
      <div className="SwimDash-totals">
        <div className="SwimDash-metric">
          <span className="SwimDash-metric-label">All-Time Distance</span>
          <strong className="SwimDash-metric-value">
            {convertDist(totalMeters).toLocaleString()}{displayUnit}
          </strong>
          <span className="SwimDash-metric-sub">{(totalMeters / 1609.344).toFixed(1)} mi</span>
        </div>
        <div className="SwimDash-metric">
          <span className="SwimDash-metric-label">Total Sessions</span>
          <strong className="SwimDash-metric-value">{totalSessions}</strong>
        </div>
        <div className="SwimDash-metric">
          <span className="SwimDash-metric-label">This Week</span>
          <strong className="SwimDash-metric-value">
            {convertDist(thisWeekMeters).toLocaleString()}{displayUnit}
          </strong>
        </div>
      </div>

      {/* PACE TREND */}
      <div className="SwimDash-section">
        <h3 className="SwimDash-section-title">
          Pace Trend
          <span className="SwimDash-section-sub">
            {' '}· median Free per 100{displayUnit} · improving = line up
          </span>
        </h3>
        {paceChartData.length < 2 ? (
          <p className="Fitness-empty SwimDash-chart-hint">
            {paceChartData.length === 0
              ? 'Log rep splits during Freestyle sets to unlock pace trends.'
              : 'Need 2+ sessions with Freestyle rep splits to show a trend.'}
          </p>
        ) : (
          <PaceChart
            sessions={paceChartData}
            displayUnit={displayUnit}
            paceToDisplay={paceToDisplay}
            convertDist={convertDist}
          />
        )}
      </div>

      {/* VOLUME */}
      <div className="SwimDash-section">
        <h3 className="SwimDash-section-title">Volume per Session</h3>
        <VolumeChart sessions={sessions} displayUnit={displayUnit} convertDist={convertDist} />
      </div>

      {/* MOVING % */}
      <div className="SwimDash-section">
        <h3 className="SwimDash-section-title">
          Moving %
          <span className="SwimDash-section-sub">
            {' '}· share of the session actually spent swimming
          </span>
        </h3>
        {moving30Avg !== null && (
          <p className="SwimDash-moving-summary">
            Last 30 days: <strong>{Math.round(moving30Avg * 100)}%</strong> average moving
            <span className="SwimDash-moving-summary-sub">
              {' '}· {moving30.length} session{moving30.length !== 1 ? 's' : ''}
            </span>
          </p>
        )}
        {movingSessions.length === 0 ? (
          <p className="Fitness-empty SwimDash-chart-hint">
            No sessions with a usable start/end time or duration yet.
          </p>
        ) : (
          <MovingChart sessions={movingSessions} />
        )}
        {movingExcluded > 0 && (
          <p className="SwimDash-moving-note">
            {movingExcluded} session{movingExcluded !== 1 ? 's' : ''} excluded — no start/end time or duration recorded.
          </p>
        )}
      </div>

      {/* FREQUENCY HEATMAP */}
      <div className="SwimDash-section">
        <h3 className="SwimDash-section-title">Swim Frequency</h3>
        <FrequencyHeatmap sessions={sessions} displayUnit={displayUnit} convertDist={convertDist} />
      </div>
    </div>
  );
}

// ── PACE CHART ───────────────────────────────────────────────────────────────

function PaceChart({ sessions, displayUnit, paceToDisplay, convertDist }) {
  const [pageOffset, setPageOffset] = useState(0); // 0 = most recent 90 days
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, text: '' });
  const showTip = (e, text) => setTip({ visible: true, x: e.clientX, y: e.clientY, text });
  const moveTip = e => setTip(t => ({ ...t, x: e.clientX, y: e.clientY }));
  const hideTip = () => setTip(t => ({ ...t, visible: false }));

  // Global Y scale — stable across pages so you can compare windows visually
  const allPaces = sessions.map(s => paceToDisplay(s.medianFreePace));
  const rawMin   = Math.min(...allPaces);
  const rawMax   = Math.max(...allPaces);
  const yPad     = (rawMax - rawMin) * 0.15 || 5;
  const yMin     = rawMin - yPad;
  const yMax     = rawMax + yPad;
  const yRange   = yMax - yMin;

  // 90-day window, date-based
  const windowEnd   = Date.now() - pageOffset * MS_90;
  const windowStart = windowEnd - MS_90;

  const windowData = sessions.filter(s => {
    const t = new Date(s.date).getTime();
    return t >= windowStart && t <= windowEnd;
  });

  const canGoForward = pageOffset > 0;
  const canGoBack    = sessions.some(s => new Date(s.date).getTime() < windowStart);

  // Date-proportional X — the full 90-day span maps to CW pixels
  function xPos(s) {
    return PAD_L + ((new Date(s.date).getTime() - windowStart) / MS_90) * CW;
  }

  function yPos(pace) {
    // Low seconds = fast = high on screen = low SVG y
    return PAD_T + ((pace - yMin) / yRange) * PCH;
  }

  const windowPaces = windowData.map(s => paceToDisplay(s.medianFreePace));

  // Split into separate segments at gaps > 14 days so layoffs show as breaks
  const lineSegments = [];
  let seg = [];
  windowData.forEach((s, i) => {
    if (i > 0 && new Date(s.date) - new Date(windowData[i - 1].date) > 14 * MS_DAY) {
      lineSegments.push(seg);
      seg = [];
    }
    seg.push(`${xPos(s).toFixed(1)},${yPos(windowPaces[i]).toFixed(1)}`);
  });
  if (seg.length) lineSegments.push(seg);

  // X-axis: tick every 30 days (0, 30, 60, 90)
  const fmtDate    = (ts, opts) => new Date(ts).toLocaleDateString(undefined, opts);
  const pagerLabel = `${fmtDate(windowStart, { month: 'short', day: 'numeric' })} – ${fmtDate(windowEnd, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="SwimDash-chart-wrap">
      <div className="SwimDash-pager">
        <button
          className="SwimDash-pager-btn"
          onClick={() => setPageOffset(p => p + 1)}
          disabled={!canGoBack}
        >◀</button>
        <span className="SwimDash-pager-label">{pagerLabel}</span>
        <button
          className="SwimDash-pager-btn"
          onClick={() => setPageOffset(p => Math.max(0, p - 1))}
          disabled={!canGoForward}
        >▶</button>
      </div>

      {windowData.length === 0 ? (
        <p className="Fitness-empty SwimDash-chart-hint" style={{ margin: '0.5rem 0' }}>
          No pace data in this window — use ◀ to see older sessions.
        </p>
      ) : (
        <svg width="100%" viewBox={`0 0 ${SVG_W} ${P_SVG_H}`} aria-label="Pace trend">
          {/* Y axis pace range labels */}
          <text x={PAD_L - 5} y={PAD_T + 10} textAnchor="end" fontSize="11" fill="#999">
            {formatPace(rawMin)}
          </text>
          <text x={PAD_L - 5} y={PAD_T + PCH} textAnchor="end" fontSize="11" fill="#999">
            {formatPace(rawMax)}
          </text>
          {/* Direction labels */}
          <text x={PAD_L + 4} y={PAD_T + 11} fontSize="9" fontWeight="700" fill="#2d7d2d">FASTER ↑</text>
          <text x={PAD_L + 4} y={PAD_T + PCH - 3} fontSize="9" fontWeight="700" fill="#8B0000">SLOWER ↓</text>
          {/* Grid lines */}
          <line x1={PAD_L} y1={PAD_T}       x2={PAD_L + CW} y2={PAD_T}       stroke="#eee" strokeWidth="1" />
          <line x1={PAD_L} y1={PAD_T + PCH} x2={PAD_L + CW} y2={PAD_T + PCH} stroke="#eee" strokeWidth="1" />
          {/* Trend line — separate polyline per continuous segment; gaps > 14 days left empty */}
          {lineSegments.map((pts, si) =>
            pts.length >= 2 ? (
              <polyline
                key={si}
                points={pts.join(' ')}
                fill="none"
                stroke="#B8860B"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ) : null
          )}
          {/* Dots */}
          {windowData.map((s, i) => {
            const dateStr = new Date(s.date).toLocaleDateString();
            let tipText;
            if (s.medianSetInfo) {
              const { stroke, perRepMeters, repTimeSec } = s.medianSetInfo;
              tipText = `${dateStr} · ${convertDist(perRepMeters)}${displayUnit} ${stroke} · ${formatRepTime(repTimeSec)}`;
            } else {
              tipText = `${dateStr} · ${formatPace(windowPaces[i])}/100${displayUnit}`;
            }
            const cx = xPos(s);
            const cy = yPos(windowPaces[i]);
            return (
              <g key={s.id}>
                <circle cx={cx} cy={cy} r="4" fill="#B8860B" stroke="#fdf6e3" strokeWidth="1.5" />
                <circle
                  cx={cx} cy={cy} r="8"
                  fill="#000" fillOpacity="0" pointerEvents="all"
                  onMouseEnter={e => showTip(e, tipText)}
                  onMouseMove={moveTip}
                  onMouseLeave={hideTip}
                />
              </g>
            );
          })}
          {/* X axis: 30-day tick marks */}
          {[0, 1, 2, 3].map(i => {
            const ts = windowStart + i * 30 * MS_DAY;
            const x  = PAD_L + (i / 3) * CW;
            return (
              <g key={i}>
                <line x1={x} y1={PAD_T + PCH} x2={x} y2={PAD_T + PCH + 4} stroke="#ccc" strokeWidth="1" />
                <text x={x} y={P_SVG_H - 6} textAnchor="middle" fontSize="10" fill="#999">
                  {fmtDate(ts, { month: 'short', day: 'numeric' })}
                </text>
              </g>
            );
          })}
        </svg>
      )}
      {tip.visible && (
        <div className="SwimDash-tooltip" style={{ left: tip.x + 12, top: tip.y < 60 ? tip.y + 16 : tip.y - 44 }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

// ── VOLUME CHART ─────────────────────────────────────────────────────────────

function VolumeChart({ sessions, displayUnit, convertDist }) {
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, text: '' });
  const showTip = (e, text) => setTip({ visible: true, x: e.clientX, y: e.clientY, text });
  const moveTip = e => setTip(t => ({ ...t, x: e.clientX, y: e.clientY }));
  const hideTip = () => setTip(t => ({ ...t, visible: false }));
  const n         = sessions.length;
  const maxMeters = Math.max(...sessions.map(s => s.totalDistanceMeters), 1);
  const slotW     = CW / n;
  const barW      = Math.max(slotW - 2, 1);

  function barX(i)   { return PAD_L + i * slotW + (slotW - barW) / 2; }
  function labelX(i) { return PAD_L + i * slotW + slotW / 2; }
  function barH(m)   { return Math.max((m / maxMeters) * VCH, 2); }

  const xLabels = labelIndices(n);

  return (
    <div className="SwimDash-chart-wrap">
      <svg width="100%" viewBox={`0 0 ${SVG_W} ${V_SVG_H}`} aria-label="Volume per session">
        <text x={PAD_L - 5} y={PAD_T + 10} textAnchor="end" fontSize="11" fill="#bbb">
          {convertDist(maxMeters).toLocaleString()}{displayUnit}
        </text>
        <text x={PAD_L - 5} y={PAD_T + VCH + 1} textAnchor="end" fontSize="11" fill="#bbb">
          0
        </text>
        <line x1={PAD_L} y1={PAD_T + VCH} x2={PAD_L + CW} y2={PAD_T + VCH} stroke="#eee" strokeWidth="1" />
        {sessions.map((s, i) => {
          const bh      = barH(s.totalDistanceMeters);
          const dateStr = new Date(s.date).toLocaleDateString();
          const distStr = `${convertDist(s.totalDistanceMeters).toLocaleString()}${displayUnit}`;
          const line1Parts = [dateStr, distStr];
          if (s.templateName) line1Parts.push(s.templateName);
          const setList = s.sets.map(set => {
            const stroke   = set.actualStroke || set.stroke || '';
            const reps     = Number(set.actualReps ?? set.reps ?? 1);
            const totalM   = Number(set.distanceMeters) || 0;
            if (totalM <= 0) return null;
            const distDisp = convertDist(totalM / reps);
            return reps > 1 ? `${reps}x${distDisp} ${stroke}` : `${distDisp} ${stroke}`;
          }).filter(Boolean).join(' · ');
          const tipText = setList ? `${line1Parts.join(' · ')}\n${setList}` : line1Parts.join(' · ');
          return (
            <g key={s.id}>
              <rect x={barX(i)} y={PAD_T + VCH - bh} width={Math.max(barW, 1)} height={bh} fill="#4B0082" rx={1} />
              <rect
                x={PAD_L + i * slotW} y={PAD_T} width={slotW} height={VCH}
                fill="#000" fillOpacity="0" pointerEvents="all"
                onMouseEnter={e => showTip(e, tipText)}
                onMouseMove={moveTip}
                onMouseLeave={hideTip}
              />
            </g>
          );
        })}
        {xLabels.map(i => (
          <text key={i} x={labelX(i)} y={V_SVG_H - 5} textAnchor="middle" fontSize="10" fill="#999">
            {new Date(sessions[i].date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
          </text>
        ))}
      </svg>
      {tip.visible && (
        <div className="SwimDash-tooltip" style={{ left: tip.x + 12, top: tip.y < 60 ? tip.y + 16 : tip.y - 44 }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

// ── MOVING % CHART ───────────────────────────────────────────────────────────
// Bars in equal slots like VolumeChart, inside the same 90-day pager as PaceChart.
// Single series, so no legend — the section title names it. Y is fixed 0–100%
// because a percentage is only comparable on an absolute scale.

const M_SVG_H = 150;
const M_PAD_B = 34;
const MCH     = M_SVG_H - PAD_T - M_PAD_B;

function MovingChart({ sessions }) {
  const [pageOffset, setPageOffset] = useState(0); // 0 = most recent 90 days
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, text: '' });
  const showTip = (e, text) => setTip({ visible: true, x: e.clientX, y: e.clientY, text });
  const moveTip = e => setTip(t => ({ ...t, x: e.clientX, y: e.clientY }));
  const hideTip = () => setTip(t => ({ ...t, visible: false }));

  const windowEnd   = Date.now() - pageOffset * MS_90;
  const windowStart = windowEnd - MS_90;
  const windowData  = sessions.filter(s => {
    const t = new Date(s.date).getTime();
    return t >= windowStart && t <= windowEnd;
  });

  const canGoForward = pageOffset > 0;
  const canGoBack    = sessions.some(s => new Date(s.date).getTime() < windowStart);

  const n     = Math.max(windowData.length, 1);
  const slotW = CW / n;
  const barW  = Math.max(slotW - 2, 1); // 2px surface gap between adjacent bars

  const fmtDate    = (ts, opts) => new Date(ts).toLocaleDateString(undefined, opts);
  const pagerLabel = `${fmtDate(windowStart, { month: 'short', day: 'numeric' })} – ${fmtDate(windowEnd, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const xLabels = labelIndices(windowData.length);

  return (
    <div className="SwimDash-chart-wrap">
      <div className="SwimDash-pager">
        <button className="SwimDash-pager-btn" onClick={() => setPageOffset(p => p + 1)} disabled={!canGoBack}>◀</button>
        <span className="SwimDash-pager-label">{pagerLabel}</span>
        <button className="SwimDash-pager-btn" onClick={() => setPageOffset(p => Math.max(0, p - 1))} disabled={!canGoForward}>▶</button>
      </div>

      {windowData.length === 0 ? (
        <p className="Fitness-empty SwimDash-chart-hint" style={{ margin: '0.5rem 0' }}>
          No moving % data in this window — use ◀ to see older sessions.
        </p>
      ) : (
        <svg width="100%" viewBox={`0 0 ${SVG_W} ${M_SVG_H}`} aria-label="Moving percent per session">
          {/* Y axis 0–100% with a 50% guide */}
          <text x={PAD_L - 5} y={PAD_T + 10} textAnchor="end" fontSize="11" fill="#bbb">100%</text>
          <text x={PAD_L - 5} y={PAD_T + MCH / 2 + 4} textAnchor="end" fontSize="11" fill="#bbb">50%</text>
          <text x={PAD_L - 5} y={PAD_T + MCH + 1} textAnchor="end" fontSize="11" fill="#bbb">0</text>
          <line x1={PAD_L} y1={PAD_T}           x2={PAD_L + CW} y2={PAD_T}           stroke="#eee" strokeWidth="1" />
          <line x1={PAD_L} y1={PAD_T + MCH / 2} x2={PAD_L + CW} y2={PAD_T + MCH / 2} stroke="#eee" strokeWidth="1" />
          <line x1={PAD_L} y1={PAD_T + MCH}     x2={PAD_L + CW} y2={PAD_T + MCH}     stroke="#eee" strokeWidth="1" />

          {windowData.map((s, i) => {
            const m       = s.moving;
            const bh      = Math.max(m.movingPct * MCH, 2);
            const dateStr = new Date(s.date).toLocaleDateString();
            const pctStr  = `${Math.round(m.movingPct * 100)}%`;
            const parts   = [
              `${dateStr} · ${pctStr} moving`,
              `${formatClock(m.swimSeconds)} swimming of ${formatClock(m.elapsedSeconds)} elapsed`,
            ];
            // Status is never color-alone — the reason is spelled out in the tooltip
            if (m.overflow) parts.push('CHECK: swim time exceeds elapsed — clamped to 100%');
            if (m.elapsedSource === 'duration') parts.push('elapsed from DurationSeconds (no start/end stamps)');
            const tipText = parts.join('\n');
            return (
              <g key={s.id}>
                <rect
                  x={PAD_L + i * slotW + (slotW - barW) / 2}
                  y={PAD_T + MCH - bh}
                  width={barW}
                  height={bh}
                  fill={m.overflow ? '#8B0000' : '#4B0082'}
                  rx={1}
                />
                <rect
                  x={PAD_L + i * slotW} y={PAD_T} width={slotW} height={MCH}
                  fill="#000" fillOpacity="0" pointerEvents="all"
                  onMouseEnter={e => showTip(e, tipText)}
                  onMouseMove={moveTip}
                  onMouseLeave={hideTip}
                />
              </g>
            );
          })}

          {xLabels.map(i => (
            <text
              key={i}
              x={PAD_L + i * slotW + slotW / 2}
              y={M_SVG_H - 5}
              textAnchor="middle" fontSize="10" fill="#999"
            >
              {new Date(windowData[i].date).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
            </text>
          ))}
        </svg>
      )}
      {tip.visible && (
        <div className="SwimDash-tooltip" style={{ left: tip.x + 12, top: tip.y < 60 ? tip.y + 16 : tip.y - 44 }}>
          {tip.text}
        </div>
      )}
    </div>
  );
}

// ── FREQUENCY HEATMAP ────────────────────────────────────────────────────────

const DOW_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function FrequencyHeatmap({ sessions, displayUnit, convertDist }) {
  const [calView, setCalView] = useState('quarter');
  const [calPage, setCalPage] = useState(0); // 0 = most recent
  const [tip, setTip] = useState({ visible: false, x: 0, y: 0, text: '' });
  const showTip = (e, text) => setTip({ visible: true, x: e.clientX, y: e.clientY, text });
  const moveTip = e => setTip(t => ({ ...t, x: e.clientX, y: e.clientY }));
  const hideTip = () => setTip(t => ({ ...t, visible: false }));

  function switchView(v) { setCalView(v); setCalPage(0); }

  // Session distance by date key
  const sessionsByDate = useMemo(() => {
    const map = new Map();
    sessions.forEach(s => {
      const k = dateKey(new Date(s.date));
      map.set(k, (map.get(k) || 0) + s.totalDistanceMeters);
    });
    return map;
  }, [sessions]);

  const { currentStreak, longestStreak } = useMemo(() => computeStreaks(sessions), [sessions]);

  // Layout constants per view
  const isQuarter   = calView === 'quarter';
  const CELL        = isQuarter ? 14 : 9;
  const GAP         = isQuarter ? 2 : 1;
  const SLOT        = CELL + GAP;
  const N_WEEKS     = isQuarter ? 13 : 53;
  const PAD_LEFT_HM = 14;
  const PAD_TOP_HM  = isQuarter ? 14 : 12;
  const HM_W        = PAD_LEFT_HM + N_WEEKS * SLOT;
  const HM_H        = PAD_TOP_HM + 7 * SLOT;

  // Compute window start Sunday
  const thisSunday = getSundayOf(new Date());
  let windowStartSunday, windowLabel;

  if (isQuarter) {
    // calPage=0: most recent 13 weeks; each page steps back 13 weeks
    const windowEndSunday = new Date(thisSunday);
    windowEndSunday.setDate(thisSunday.getDate() - calPage * N_WEEKS * 7);

    windowStartSunday = new Date(windowEndSunday);
    windowStartSunday.setDate(windowEndSunday.getDate() - (N_WEEKS - 1) * 7);

    const lastDay = new Date(windowEndSunday);
    lastDay.setDate(windowEndSunday.getDate() + 6);
    const s1 = windowStartSunday.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    const s2 = lastDay.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    windowLabel = s1 === s2
      ? s1
      : `${windowStartSunday.toLocaleDateString(undefined, { month: 'short' })} – ${s2}`;
  } else {
    // Year view: show calendar year, calPage=0 = current year
    const windowYear = new Date().getFullYear() - calPage;
    const jan1       = new Date(windowYear, 0, 1);
    windowStartSunday = getSundayOf(jan1);
    windowLabel = String(windowYear);
  }

  const oldestDate   = sessions.length > 0 ? new Date(sessions[0].date) : new Date();
  const canGoBack    = windowStartSunday.getTime() > oldestDate.getTime();
  const canGoForward = calPage > 0;

  // Build month labels: mark the column where each month begins
  const monthLabels = [];
  for (let col = 0; col < N_WEEKS; col++) {
    const weekSun = new Date(windowStartSunday);
    weekSun.setDate(windowStartSunday.getDate() + col * 7);
    for (let d = 0; d < 7; d++) {
      const day = new Date(weekSun);
      day.setDate(weekSun.getDate() + d);
      if (day.getDate() === 1) {
        const m = day.getMonth(); // 0 = Jan
        // Year view: only even months (Jan=0, Mar=2, May=4, Jul=6, Sep=8, Nov=10)
        if (!isQuarter && m % 2 !== 0) break;
        monthLabels.push({ col, label: day.toLocaleDateString(undefined, { month: 'short' }) });
        break;
      }
    }
  }

  return (
    <div className="SwimDash-heatmap">
      <div className="SwimDash-heatmap-toolbar">
        <div className="SwimDash-streak-row">
          <span className="SwimDash-streak-pill">Current: {currentStreak} active wks</span>
          <span className="SwimDash-streak-pill">Longest: {longestStreak} active wks</span>
        </div>
        <div className="SwimDash-view-toggle">
          <button
            className={`Fitness-btn Fitness-btn--small${isQuarter ? ' Fitness-btn--active' : ' Fitness-btn--secondary'}`}
            onClick={() => switchView('quarter')}
          >3-Month</button>
          <button
            className={`Fitness-btn Fitness-btn--small${!isQuarter ? ' Fitness-btn--active' : ' Fitness-btn--secondary'}`}
            onClick={() => switchView('year')}
          >Year</button>
        </div>
      </div>

      <div className="SwimDash-pager">
        <button className="SwimDash-pager-btn" onClick={() => setCalPage(p => p + 1)} disabled={!canGoBack}>◀</button>
        <span className="SwimDash-pager-label">{windowLabel}</span>
        <button className="SwimDash-pager-btn" onClick={() => setCalPage(p => Math.max(0, p - 1))} disabled={!canGoForward}>▶</button>
      </div>

      <div className="SwimDash-chart-wrap">
        <svg width="100%" viewBox={`0 0 ${HM_W} ${HM_H}`} aria-label="Swim frequency heatmap">
          {/* Month labels */}
          {monthLabels.map(({ col, label }) => (
            <text
              key={`m-${col}`}
              x={PAD_LEFT_HM + col * SLOT}
              y={PAD_TOP_HM - 2}
              fontSize={isQuarter ? 9 : 7}
              fill="#999"
            >{label}</text>
          ))}
          {/* Day-of-week labels */}
          {DOW_LABELS.map((lbl, row) => (
            <text
              key={`dow-${row}`}
              x={PAD_LEFT_HM - 2}
              y={PAD_TOP_HM + row * SLOT + Math.round(CELL * 0.75)}
              textAnchor="end"
              fontSize={isQuarter ? 9 : 7}
              fill="#ccc"
            >{lbl}</text>
          ))}
          {/* Cells — flat iteration avoids nested array fragments */}
          {Array.from({ length: N_WEEKS * 7 }, (_, idx) => {
            const col      = Math.floor(idx / 7);
            const row      = idx % 7;
            const cellDate = new Date(windowStartSunday);
            cellDate.setDate(windowStartSunday.getDate() + col * 7 + row);
            const k        = dateKey(cellDate);
            const meters   = sessionsByDate.get(k) || 0;
            const color    = swimColor(meters) || '#fdf6e3';
            const x        = PAD_LEFT_HM + col * SLOT;
            const y        = PAD_TOP_HM + row * SLOT;
            const tipText  = meters > 0
              ? `${cellDate.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })} · ${convertDist(meters).toLocaleString()}${displayUnit}`
              : '';
            return (
              <g key={idx}>
                <rect
                  x={x} y={y} width={CELL} height={CELL} fill={color} opacity={meters > 0 ? 1 : 0.55} rx={1}
                  onMouseEnter={tipText ? e => showTip(e, tipText) : undefined}
                  onMouseMove={tipText ? moveTip : undefined}
                  onMouseLeave={tipText ? hideTip : undefined}
                />
              </g>
            );
          })}
        </svg>
        {tip.visible && (
          <div className="SwimDash-tooltip" style={{ left: tip.x + 12, top: tip.y < 60 ? tip.y + 16 : tip.y - 44 }}>
            {tip.text}
          </div>
        )}
      </div>

      {/* Color legend */}
      <div className="SwimDash-legend">
        <span className="SwimDash-legend-label">Less</span>
        {['#c4a8e0', '#9b6fd4', '#6b35c2', '#4B0082'].map(c => (
          <span key={c} className="SwimDash-legend-cell" style={{ background: c }} />
        ))}
        <span className="SwimDash-legend-label">More</span>
        <span className="SwimDash-legend-label" style={{ marginLeft: '0.5rem', color: '#bbb' }}>
          ≤500 / 501–1100 / 1101–1800 / 1801+m
        </span>
      </div>
    </div>
  );
}

export default SwimmingDashboard;
